/**
 * Employee API Service
 * Handles employee-related API calls
 */

import API_BASE_URL from './apiConfig';
import { getAuthHeaders } from './authApi';

/**
 * Fetch all employees
 * @returns {Promise<{success: boolean, data?: array, error?: string}>}
 */
export const fetchEmployees = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/employee`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.message || `HTTP error! status: ${response.status}`,
      };
    }

    return {
      success: true,
      data: Array.isArray(result) ? result : [],
    };
  } catch (error) {
    console.error('Error fetching employees:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch employees',
    };
  }
};

/**
 * Fetch single employee by ID
 * @param {string} id - Employee ID
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const fetchEmployeeById = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/employee/single/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.message || `HTTP error! status: ${response.status}`,
      };
    }

    return {
      success: true,
      data: result,
    };
  } catch (error) {
    console.error('Error fetching employee:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch employee',
    };
  }
};

/**
 * Create or update employee
 * @param {object} employeeData - Employee data
 * @param {string|null} id - Employee ID for update (null for create)
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const saveEmployee = async (employeeData, id = null) => {
  try {
    const url = id ? `${API_BASE_URL}/employee/single/${id}` : `${API_BASE_URL}/employee`;
    const method = id ? 'PUT' : 'POST';

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(employeeData),
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.message || `HTTP error! status: ${response.status}`,
      };
    }

    return {
      success: true,
      data: result,
    };
  } catch (error) {
    console.error('Error saving employee:', error);
    return {
      success: false,
      error: error.message || 'Failed to save employee',
    };
  }
};

/**
 * Delete employee
 * @param {string} id - Employee ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteEmployee = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/employee/single/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });

    if (!response.ok) {
      const result = await response.json();
      return {
        success: false,
        error: result.message || `HTTP error! status: ${response.status}`,
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    console.error('Error deleting employee:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete employee',
    };
  }
};
